import { formatDistanceToNowStrict } from 'date-fns';
import MY_TICKET_SECTION_TYPES, { TICKET_COUNT_FILTER_CONFIG, TICKET_OPEN_STATUS, TICKET_STATUS_LABEL } from './constants';

const PRIORITY_LABELS = {
    4: 'Critical',
    3: 'High',
    2: 'Medium',
    1: 'Low'
};

const formatTimeAgo = (timestamp) => {
    if (!timestamp) return '';
    try {
        return formatDistanceToNowStrict(new Date(timestamp), { addSuffix: true });
    } catch (e) {
        return '';
    }
};

const isOpenTicket = (type, item = {}) => {
    const config = TICKET_COUNT_FILTER_CONFIG[type];
    if (!config) return false;
    return config.statuses.includes(item?.[config.statusKey]);
};

const getTicketStatus = (type, item = {}) => {
    const config = TICKET_COUNT_FILTER_CONFIG[type];
    const rawStatus = String(item?.[config?.statusKey] || '')
        .trim()
        .toLowerCase();
    if (rawStatus === TICKET_OPEN_STATUS.OPEN) return TICKET_OPEN_STATUS.OPEN;
    if (rawStatus === TICKET_OPEN_STATUS.CLOSE || rawStatus === 'closed') return TICKET_OPEN_STATUS.CLOSE;
    return rawStatus || '';
};

const mapBookingRequestItem = (item = {}) => {
    const {
        cancellation_code: refId = '',
        booking_code: bookingCode = '',
        buyer = '',
        vendor_organization: vendorOrganization = '',
        member_remarks: memberRemarks = '',
        latest_comment: latestComment = null,
        request_type: requestType = '',
        category = {},
        updated_at: updatedAt = '',
        created_at: createdAt = '',
        status = ''
    } = item;

    const commentText = typeof latestComment === 'string' ? latestComment : latestComment?.remarks || latestComment?.internal_comment || '';

    const description = memberRemarks || commentText || [requestType, bookingCode].filter(Boolean).join(' · ') || status || '';
    const ticketStatus = getTicketStatus(MY_TICKET_SECTION_TYPES.BOOKING_REQUEST, item);

    return {
        id: refId || bookingCode,
        agencyName: vendorOrganization || buyer || bookingCode || 'Booking request',
        refId,
        timeAgo: formatTimeAgo(updatedAt || createdAt),
        description,
        priority: '',
        category: category?.name || '',
        status: ticketStatus,
        statusLabel: TICKET_STATUS_LABEL[ticketStatus] || '',
        isUnread: isOpenTicket(MY_TICKET_SECTION_TYPES.BOOKING_REQUEST, item),
        raw: item
    };
};

const mapOcSystemItem = (item = {}, type = MY_TICKET_SECTION_TYPES.INTERNAL_QUERIES) => {
    const {
        id,
        code: refId = '',
        member_name: memberName = '',
        organization = '',
        request_type: requestType = {},
        request_type_code: requestTypeCode = '',
        request_description_or_state: requestDescription = '',
        conversation = null,
        category = {},
        priority_type: priorityType = null,
        updated_at: updatedAt = '',
        created_at: createdAt = ''
    } = item;

    const remarks = typeof conversation?.remarks === 'string' ? conversation.remarks.trim() : '';
    const givenBy = typeof conversation?.given_by === 'string' ? conversation.given_by.trim() : '';
    const raisedFor = typeof requestDescription === 'string' ? requestDescription.trim() : '';

    let description = '';
    if (remarks) {
        description = givenBy ? `${givenBy}:  ${remarks}` : remarks;
    } else if (raisedFor) {
        description = raisedFor;
    } else {
        description = [requestType?.name, requestTypeCode].filter(Boolean).join(' · ');
    }

    const ticketStatus = getTicketStatus(type, item);

    return {
        id: id || refId,
        agencyName: organization || memberName || requestType?.name || 'Ticket',
        refId,
        timeAgo: formatTimeAgo(updatedAt || createdAt),
        description,
        priority: PRIORITY_LABELS[priorityType] || '',
        priorityId: priorityType || '',
        category: category?.name || '',
        status: ticketStatus,
        statusLabel: TICKET_STATUS_LABEL[ticketStatus] || '',
        isUnread: isOpenTicket(type, item),
        raw: item
    };
};

export const mapTicketItems = (type, items = []) => {
    if (!Array.isArray(items)) return [];
    if (type === MY_TICKET_SECTION_TYPES.BOOKING_REQUEST) {
        return items.map(mapBookingRequestItem);
    }
    return items.map((item) => mapOcSystemItem(item, type));
};

export const extractListFromResponse = (type, data = {}) => {
    const results = data?.results || {};
    if (type === MY_TICKET_SECTION_TYPES.BOOKING_REQUEST) {
        return Array.isArray(results.service_request_list) ? results.service_request_list : [];
    }
    return Array.isArray(results.open_close_system) ? results.open_close_system : [];
};

export const normalizeTabPayload = (type, data = {}, { replace = true, existingItems = [] } = {}) => {
    const list = extractListFromResponse(type, data);
    const next = data?.next ?? null;
    const previous = data?.previous ?? null;
    const count = typeof data?.count === 'number' ? data.count : list.length;

    return {
        items: replace ? list : [...existingItems, ...list],
        next,
        previous,
        count,
        hasMore: Boolean(next),
        isLoaded: true
    };
};

/**
 * Detail page URLs — same paths already used elsewhere in Aryabhatta.
 * Booking: /booking-list/:bookingCode?serviceRequestCode=
 * OC tabs: /internal-commenting/:code?queryType=2|1
 */
export const getTicketDetailUrl = (type, raw = {}) => {
    if (!raw || typeof raw !== 'object') return '';

    if (type === MY_TICKET_SECTION_TYPES.BOOKING_REQUEST) {
        const bookingCode = raw.booking_code;
        const serviceRequestCode = raw.cancellation_code;
        if (!bookingCode || !serviceRequestCode) return '';
        return `/booking-list/${bookingCode}?serviceRequestCode=${serviceRequestCode}`;
    }

    const code = raw.code;
    if (!code) return '';

    if (type === MY_TICKET_SECTION_TYPES.INTERNAL_QUERIES) {
        return `/internal-commenting/${code}?queryType=2`;
    }

    if (type === MY_TICKET_SECTION_TYPES.TRAVCLAN_HELP) {
        return `/internal-commenting/${code}?queryType=1`;
    }

    return '';
};
